import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Controller, Control, FieldErrors, useWatch, Path } from 'react-hook-form';
import { InputAdornment, Box, ListSubheader, CircularProgress, Divider } from '@mui/material';
import axios from 'axios';

import { getAsyncDictionary } from 'requests/dictionary';
import { EDictionaryTypes } from 'models/dictionary';
import { IFormElementOptionProp, IFormElementProps } from 'models/form';
import { StyledFormElementWrapper } from 'theme/styles';
import FormLabel from 'components/atoms/FormLabel';
import FormErrorLabel from 'components/atoms/FormErrorLabel';
import Icon from 'components/atoms/Icon/Icon';
import { EIconTypes } from 'constants/Icons';
import { useTranslations } from 'hooks/useTranslations';
import { useDebounce } from 'hooks/useDebounce';
import { colors } from 'theme/colors';

import {
  StyledFormControl,
  StyledInnerWrapper,
  StyledMultiSelect,
  StyledSearchMenuItem,
  StyledSearchTextField,
  StyledClearButton,
  StyledCheckbox,
  StyledListItemText,
  StyledOptionMenuItem,
  getMenuProps,
} from './styles';

const LIMIT = 25;
export interface ILoadOptionsResult {
  content: IFormElementOptionProp[];
  last: boolean;
}

interface IFormAsyncMultiAutocomplete<
  T extends Record<string, any>,
> extends IFormElementProps {
  dictionaryType?: EDictionaryTypes;
  loadOptions?: (currentPage?: number, search?: string) => Promise<ILoadOptionsResult>;
  externalLastElements?: boolean;
  setSelected?: (arg: IFormElementOptionProp[]) => void;
  control: Control<T>;
  errors: FieldErrors<T>;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  canBeEmpty?: boolean;
  keepSelectedInPlace?: boolean;
}

export const FormAsyncMultiAutocomplete = <T extends Record<string, any>>({
  name,
  label,
  dictionaryType,
  loadOptions: loadOptionsProp,
  externalLastElements,
  setSelected,
  control,
  errors,
  disabled,
  required,
  className,
  canBeEmpty = true,
  keepSelectedInPlace = false,
}: IFormAsyncMultiAutocomplete<T>) => {
  const { t } = useTranslations();
  const [options, setOptions] = useState<IFormElementOptionProp[]>([]);
  const [page, setPage] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState<IFormElementOptionProp[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loadingRef = useRef(false);
  const initialSelectionAppliedRef = useRef(false);

  const debouncedQuery = useDebounce(query, 300);

  const abortRef = useRef<AbortController | null>(null);
  const requestIdRef = useRef(0);
  const selectRef = useRef<HTMLDivElement>(null);

  const value = useWatch({
    control,
    name: name as Path<T>,
  });

  useEffect(() => {
    if (externalLastElements !== undefined) {
      setHasMore(!externalLastElements);
    }
  }, [externalLastElements]);

  useEffect(() => {
    if (!value || value.length === 0) {
      setSelectedOptions([]);
      if (!open) {
        setQuery('');
        setPage(0);
      }
    }
  }, [value]);

  useEffect(() => {
    if (initialSelectionAppliedRef.current) {
      return;
    }
    if (!Array.isArray(value) || value.length === 0 || !options.length) {
      return;
    }
    const seed = options.filter((option) => value.includes(option.value));
    if (seed.length) {
      setSelectedOptions(seed);
      initialSelectionAppliedRef.current = true;
    }
  }, [options, value]);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '23px';
    } else {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '0';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const loadOptions = useCallback(
    async (pageToLoad: number, reset: boolean, search: string) => {
      const requestId = ++requestIdRef.current;

      if (abortRef.current) abortRef.current.abort();

      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);

      try {
        let mapped: IFormElementOptionProp[];

        if (loadOptionsProp) {
          const result = await loadOptionsProp(pageToLoad, search);
          if (requestId !== requestIdRef.current) return;
          mapped = result.content;
          setHasMore(!result.last);
        } else {
          if (!dictionaryType) return;
          const data = await getAsyncDictionary(
            dictionaryType,
            pageToLoad,
            LIMIT,
            search,
            controller.signal,
          );

          if (!data || requestId !== requestIdRef.current) return;

          mapped = data.content.map((item: any) => ({
            label: item.name,
            value: item.uniqueId,
          }));
          setHasMore(mapped.length < data.totalElements);
        }

        if (reset) setOptions(mapped);
        else setOptions((prev) => [...prev, ...mapped]);
      } catch (error: any) {
        if (!axios.isCancel(error)) console.error(error);
      } finally {
        setLoading(false);
      }
    },
    [dictionaryType, loadOptionsProp],
  );

  useEffect(() => {
    setPage(0);
    setHasMore(true);
    loadOptions(0, true, debouncedQuery);
  }, [debouncedQuery]);

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    const target = event.currentTarget;
    const hasScrollbar = target.scrollHeight > target.clientHeight;
    if (!hasScrollbar) {
      return;
    }
    const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 50;

    if (isNearBottom && !loadingRef.current && hasMore) {
      setPage((prevPage) => {
        const nextPage = prevPage + 1;
        loadOptions(nextPage, false, debouncedQuery);
        return nextPage;
      });
    }
  };

  useEffect(() => {
    if (setSelected) {
      setSelected(selectedOptions);
    }
  }, [selectedOptions]);

  return (
    <StyledFormElementWrapper className={className}>
      <FormLabel name={name} label={label} disabled={disabled} required={required} />
      <StyledInnerWrapper>
        <Controller
          name={name as any}
          control={control}
          render={({ field }) => {
            return (
              <StyledFormControl ref={selectRef}>
                <StyledMultiSelect
                  {...field}
                  multiple
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (!Array.isArray(value)) return;
                    field.onChange(value.filter(Boolean));
                  }}
                  open={open}
                  onOpen={() => setOpen(true)}
                  onClose={() => setOpen(false)}
                  disabled={disabled}
                  displayEmpty
                  isEmpty={!selectedOptions.length}
                  renderValue={(selected) => {
                    const selectedValues = selected as string[];
                    if (!selectedValues?.length) return t('formSelect.select.placeholder');

                    return `${selectedValues.length} ${t('formSelect.selected.text')}`;
                  }}
                  endAdornment={
                    field.value?.length && canBeEmpty && !disabled ? (
                      <StyledClearButton
                        onClick={() => {
                          field.onChange([]);
                          setSelectedOptions([]);
                        }}
                      >
                        <Icon type={EIconTypes.clear} color={colors.gray900} />
                      </StyledClearButton>
                    ) : null
                  }
                  MenuProps={getMenuProps(selectRef.current?.offsetWidth)}
                >
                  <StyledSearchMenuItem disableRipple>
                    <StyledSearchTextField
                      placeholder={`${t('formSelect.search.placeholder')}...`}
                      value={query}
                      onChange={(e) => setQuery(e.target.value ?? '')}
                      slotProps={{
                        input: {
                          endAdornment: (
                            <InputAdornment position="end">
                              {loading ? (
                                <CircularProgress size={18} />
                              ) : (
                                <Icon type={EIconTypes.search} color={colors.gray900} />
                              )}
                            </InputAdornment>
                          ),
                        },
                      }}
                    />
                  </StyledSearchMenuItem>
                  {!keepSelectedInPlace && (
                    <Box>
                      {Array.isArray(field.value) ? (
                        <div>
                          {selectedOptions.map((option: any) => {
                            const currentValue: string[] = field.value || [];
                            return (
                              <StyledOptionMenuItem
                                key={`selected_${name}_${option.value}`}
                                onClick={() => {
                                  const newValue = currentValue.filter(
                                    (v: string) => v !== option.value,
                                  );
                                  field.onChange(newValue);
                                  setSelectedOptions((prevSelectedOptions) =>
                                    prevSelectedOptions.filter(
                                      (v) => v.value !== option.value,
                                    ),
                                  );
                                }}
                              >
                                <StyledCheckbox checked />
                                <StyledListItemText primary={option.label} />
                              </StyledOptionMenuItem>
                            );
                          })}
                        </div>
                      ) : (
                        ''
                      )}
                      <Divider />
                    </Box>
                  )}
                  <ListSubheader
                    component="div"
                    disableSticky
                    style={{ padding: 0, overflow: 'hidden' }}
                  >
                    <Box
                      sx={{ maxHeight: 268, overflowY: 'auto', overflowX: 'hidden' }}
                      onScroll={handleScroll}
                    >
                      {keepSelectedInPlace &&
                        selectedOptions
                          .filter(
                            (option) =>
                              !options.some((op) => String(op.value) === String(option.value)),
                          )
                          .map((option) => {
                            const value = String(option.value);
                            const currentValue: string[] = field.value || [];
                            return (
                              <StyledOptionMenuItem
                                key={`selected_${name}_${value}`}
                                onClick={() => {
                                  field.onChange(currentValue.filter((v) => v !== value));
                                  setSelectedOptions((prevSelectedOptions) =>
                                    prevSelectedOptions.filter(
                                      (v) => String(v.value) !== value,
                                    ),
                                  );
                                }}
                              >
                                <StyledCheckbox checked />
                                <StyledListItemText primary={option.label} />
                              </StyledOptionMenuItem>
                            );
                          })}
                      {options.map((option) => {
                        const value = String(option.value);
                        const currentValue: string[] = field.value || [];
                        const checked = currentValue.includes(value);
                        if (checked && !keepSelectedInPlace) {
                          return null;
                        }
                        return (
                          <StyledOptionMenuItem
                            key={`option_${name}_${value}`}
                            onClick={() => {
                              if (checked) {
                                field.onChange(currentValue.filter((v) => v !== value));
                                setSelectedOptions((prevSelectedOptions) =>
                                  prevSelectedOptions.filter((v) => String(v.value) !== value),
                                );
                              } else {
                                field.onChange([...currentValue, value]);
                                setSelectedOptions((prevSelectedOptions) => [
                                  ...prevSelectedOptions,
                                  option,
                                ]);
                              }
                            }}
                          >
                            <StyledCheckbox checked={checked} />
                            <StyledListItemText primary={option.label} />
                          </StyledOptionMenuItem>
                        );
                      })}

                      {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 1 }}>
                          <CircularProgress size={20} />
                        </Box>
                      )}
                    </Box>
                  </ListSubheader>
                </StyledMultiSelect>
              </StyledFormControl>
            );
          }}
        />
      </StyledInnerWrapper>
      <FormErrorLabel label={errors[name]?.message as string} />
    </StyledFormElementWrapper>
  );
};
